import type { PropsWithChildren } from 'react';
import React, { createContext, useContext, useMemo } from 'react';

import type {
  LayerManager,
  LocaleService,
  TrackingService,
  VueFormatterServiceInterface,
} from './feature-service-types';
import type { Logger } from '@feature-hub/logger';

interface FeatureAppEnv {
  featureAppId: string;
  featureAppName?: string;
}

interface ContextState {
  readonly featureAppEnv: FeatureAppEnv;
  readonly localeService?: LocaleService;
  readonly logger?: Logger;
  readonly trackingService?: TrackingService;
  readonly vueFormatterService?: VueFormatterServiceInterface;
  readonly layerManager?: LayerManager;
}

export const Context = createContext<ContextState>({} as ContextState);

export interface ContextProviderProps {
  readonly featureAppEnv: FeatureAppEnv;
  readonly localeService?: LocaleService;
  readonly logger?: Logger;
  readonly trackingService?: TrackingService;
  readonly vueFormatterService?: VueFormatterServiceInterface;
  readonly layerManager?: LayerManager;
}

export const ContextProvider = ({
  children,
  featureAppEnv,
  localeService,
  logger,
  trackingService,
  vueFormatterService,
  layerManager,
}: PropsWithChildren<ContextProviderProps>) => {
  const value = useMemo(
    () => ({
      featureAppEnv,
      localeService,
      logger,
      trackingService,
      vueFormatterService,
      layerManager,
    }),
    [],
  );
  return <Context.Provider value={value}>{children}</Context.Provider>;
};

export const useLogger = () => useContext(Context).logger;
